import axios, { AxiosInstance } from 'axios';
import { store } from '../store';
import { coordinatedRefresh, forceLogout } from './authSession';

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach workspace designation so backend can audit and scope by workspace
    const ws = state.workspace;
    if (ws?.active && ws.designationId) {
      config.headers['X-Workspace-Designation'] = ws.designationId;
    }
    // Multi-branch (Phase 3): a Branch Manager's chosen branch rides on every request. The server
    // checks it against their assignments and answers 403 BRANCH_ACCESS_REVOKED if it is not theirs;
    // without the header the server uses their primary branch, exactly as before this feature.
    const br = state.branch;
    if (br?.isManager && br.activeBranchId) {
      config.headers['X-Branch-Id'] = br.activeBranchId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/** Stable code the backend sends when the selected branch is no longer the caller's. */
const BRANCH_ACCESS_REVOKED = 'BRANCH_ACCESS_REVOKED';

// Backup refresh path: if the proactive timer missed (tab slept, clock drift, etc.)
// a 401 triggers the SAME coordinated refresh (cross-tab lock + in-tab single-flight),
// so it never races the proactive refresh.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The branch this tab was working in has been taken away (Super Admin removed it while the
    // person was logged in). NOT a logout: the branch slice drops back to the primary and the
    // provider shows what happened. Dispatched as a plain action so this module never imports
    // the slice (that would create an import cycle through the store).
    if (error.response?.status === 403 && error.response?.data?.errors?.code === BRANCH_ACCESS_REVOKED) {
      store.dispatch({ type: 'branch/accessRevoked', payload: { branchId: error.response.data.errors.branchId ?? null } });
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // No refresh token at all → nothing to recover, log out.
      if (!store.getState().auth.refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const accessToken = await coordinatedRefresh();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError: any) {
        // Only a DEFINITIVE rejection ends the session - the server looked at the refresh token
        // and said no (revoked, reused, expired, or none held). A network drop, a timeout or a
        // 5xx means we never got an answer, so the tokens are kept and the next request tries
        // again; the person stays signed in until they click Logout.
        const status = refreshError?.response?.status;
        const definitive = status === 400 || status === 401 || status === 403
          || refreshError?.message === 'NO_REFRESH_TOKEN';
        if (definitive) forceLogout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
